import React, { useMemo, useState } from 'react';
import {
  startOfDay, subDays, addDays,
  parseISO, format
} from 'date-fns';
import './datatable.css';

const PAGE_OPTS  = [25, 50, 100];
const todayStart = startOfDay(new Date());

const PRESETS = {
  today : { from: todayStart,             to: addDays(todayStart, 1) },
  week  : { from: subDays(todayStart, 6), to: addDays(todayStart, 1) },
  month : { from: subDays(todayStart,29), to: addDays(todayStart, 1) },
};

export default function DataTable({ columns, rows, searchKeys = [], dateKey }) {
  /* ───── table state ───── */
  const [filter, setFilter] = useState('');
  const [pageSz, setPageSz] = useState(PAGE_OPTS[0]);
  const [page,   setPage]   = useState(1);

  /* date-filter state */
  const [mode,  setMode]  = useState('all');         // all | today | week | month | range
  const [range, setRange] = useState(PRESETS.today); // keeps custom range too

  /* ---------- helpers ---------- */
  const matchesSearch = (row) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return searchKeys.some(k => String(row?.[k] ?? '')
      .toLowerCase()
      .includes(q));
  };

  const matchesDate = (row) => {
    if (mode === 'all' || !dateKey) return true;
    const raw = row[dateKey];
    if (!raw) return false;

    // safe parse (supports ISO string or Date)
    const ts = raw instanceof Date ? raw : parseISO(String(raw));
    if (isNaN(+ts)) return false;

    // inclusive start, exclusive end → [from, to)
    return +ts >= +range.from && +ts < +range.to;
  };

  /* ---------- filtered list ---------- */
  const filtered = useMemo(() => {
    const out = rows
      .filter(matchesSearch)
      .filter(matchesDate);

    // newest first if dateKey provided
    if (dateKey) {
      out.sort((a, b) => +new Date(b[dateKey]) - +new Date(a[dateKey]));
    }
    return out;
  }, [rows, filter, mode, range, dateKey]);

  /* ---------- pagination ---------- */
  const total = Math.max(1, Math.ceil(filtered.length / pageSz));
  const slice = filtered.slice((page-1)*pageSz, page*pageSz);

  /* date-range helpers */
  const pickPreset = (k) => {
    setMode(k);
    if (k !== 'all') setRange(PRESETS[k]);
    setPage(1);
  };
  const pickCustom = (which, value) => {
    const dt = value ? new Date(value) : todayStart;
    setRange(r => ({ ...r, [which]: dt }));
    setMode('range');
    setPage(1);
  };

  return (
    <>
      {/* ── Toolbar: search + date filter ── */}
      <div className="dt-toolbar">
        <input
          placeholder="Search…"
          value={filter}
          onChange={e => { setFilter(e.target.value); setPage(1); }}
          aria-label="Search rows"
        />

        {dateKey && (
          <div className="dt-datebar" role="group" aria-label="Date range">
            {['all','today','week','month'].map(p => (
              <button
                key={p}
                className={mode === p ? 'active' : ''}
                onClick={() => pickPreset(p)}
                type="button"
              >
                {p.toUpperCase()}
              </button>
            ))}

            <span className="custom">
              <input
                type="date"
                value={format(range.from, 'yyyy-MM-dd')}
                onChange={e => pickCustom('from', e.target.value)}
                aria-label="From date"
              />
              &nbsp;→&nbsp;
              <input
                type="date"
                value={format(range.to, 'yyyy-MM-dd')}
                onChange={e => pickCustom('to', e.target.value)}
                aria-label="To date"
              />
            </span>
          </div>
        )}
      </div>

      {/* ── Table (becomes stacked cards on mobile) ── */}
      <div className="table-container prettified responsive-table">
        <table>
          <thead>
            <tr>
              {columns.map(c => <th key={c.header}>{c.header}</th>)}
            </tr>
          </thead>
          <tbody>
            {slice.length ? slice.map((row, i) => (
              <tr key={row._id ?? i}>
                {columns.map(c => (
                  <td key={c.header} data-th={c.header}>
                    {typeof c.cell === 'function' ? c.cell(row) : row[c.cell]}
                  </td>
                ))}
              </tr>
            )) : (
              <tr>
                <td colSpan={columns.length} className="no-data">No rows</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination footer ── */}
      <div className="dt-pagination">
        <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>Prev</button>
        <span>Page {page} / {total}</span>
        <button onClick={() => setPage(p => Math.min(total, p+1))} disabled={page === total}>Next</button>

        <select
          value={pageSz}
          onChange={e => { setPageSz(+e.target.value); setPage(1); }}
          aria-label="Rows per page"
        >
          {PAGE_OPTS.map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
      </div>
    </>
  );
}
